
window._erj_.clearAccountInfo = () => {
  sessionStorage.removeItem("accountData");
};

const TIER_ORDER = ["silver", "gold", "diamond", "ambassador"];

// Ball center positions on the progress track (% of track width), measured from
// the "Progress Track" SVGs in the Figma account header design.
const TRACK_POSITIONS = { silver: 13.9, gold: 32.3, diamond: 57.8, ambassador: 92.4 };

// USD spend required to reach each tier; gold is first-purchase, not money-based.
const TIER_THRESHOLDS = { silver: 0, gold: 0, diamond: 150, ambassador: 400 };

// Fill fallback per tier (% of track width) copied from the Figma mocks, used
// when the customer's spend cannot drive the interpolation (silver tier, or
// missing/unparseable profile.totalSpent).
const STATIC_FILL = { silver: 28.9, gold: 40.8, diamond: 79.0, ambassador: 92.4 };

function getProgressFillPercent(level, totalSpentUsd) {
  const index = TIER_ORDER.indexOf(level);
  if (index <= 0) return STATIC_FILL.silver;
  if (index === TIER_ORDER.length - 1) return TRACK_POSITIONS.ambassador;

  const currentThreshold = TIER_THRESHOLDS[level];
  const nextLevel = TIER_ORDER[index + 1];
  const nextThreshold = TIER_THRESHOLDS[nextLevel];
  if (!Number.isFinite(totalSpentUsd) || nextThreshold <= currentThreshold) {
    return STATIC_FILL[level];
  }
  const ratio = Math.min(1, Math.max(0, (totalSpentUsd - currentThreshold) / (nextThreshold - currentThreshold)));
  return TRACK_POSITIONS[level] + (TRACK_POSITIONS[nextLevel] - TRACK_POSITIONS[level]) * ratio;
}

function formatLocalMoney(usdAmount) {
  const converted = window._erj_?.convertUsdAndRoundUp?.(usdAmount);
  if (!Number.isFinite(converted) || converted <= 0) return null;
  const format = window._erj_?.getNoDecimalsFormat?.();
  if (typeof window.Shopify?.formatMoney !== "function" || !format) return null;
  return window.Shopify.formatMoney(Math.round(converted * 100), format).replace(/<[^>]*>/gm, "");
}

function buildProgressMessage(level, totalSpentUsd, progressLocale, tiersLocale, esc) {
  if (!progressLocale) return "";
  if (level === "ambassador") {
    return esc(progressLocale.top_tier || "");
  }
  if (level === "silver") {
    return (progressLocale.purchase_away || "")
      .replace("{tier}", tiersLocale?.gold || "Gold");
  }

  const nextLevel = level === "gold" ? "diamond" : "ambassador";
  const gapUsd = TIER_THRESHOLDS[nextLevel] - totalSpentUsd;
  if (!Number.isFinite(gapUsd) || gapUsd <= 0) return "";

  const money = formatLocalMoney(gapUsd);
  if (!money) return "";
  return esc(progressLocale.money_away || "")
    .replace("{tier}", esc(tiersLocale?.[nextLevel] || nextLevel))
    .replace("{money}", money);
}

function buildInfoTipHtml(progressLocale, esc) {
  const title = progressLocale?.tip_title;
  const body = progressLocale?.tip_body;
  const linkText = progressLocale?.tip_link;
  if (!title || !body || !linkText) return "";

  const membershipUrl = window._erj_?.membershipPageUrl || "/pages/rewards#member-benefits";
  return `<span class="vip-info-tip" tabindex="0" role="button" aria-label="${esc(title)}">
    <svg class="vip-progress__msg-icon" viewBox="0 0 10 10" fill="currentColor" aria-hidden="true"><path d="M6.83586 9.64849C5.66101 10.0991 4.33236 10.1186 3.1448 9.69411C2.57447 9.49024 2.06114 9.16625 1.61306 8.75863C0.987471 8.1894 0.506831 7.47366 0.23913 6.66844C0.0313058 6.0427 -0.033187 5.38334 0.0153385 4.72761C0.0818272 3.82751 0.3877 2.9419 0.901647 2.20405C1.04111 2.00393 1.18369 1.82231 1.35247 1.64256C1.63315 1.34369 1.94551 1.0762 2.28693 0.846705C3.17923 0.246595 4.23806 -0.055272 5.3126 0.00835139C6.04884 0.0518503 6.78646 0.27672 7.42415 0.642336C8.14605 1.0562 8.74145 1.64019 9.1919 2.34079C9.90244 3.44577 10.1597 4.80273 9.90282 6.09395C9.58684 7.68303 8.33927 9.07187 6.83586 9.64861V9.64849ZM7.49114 8.51814C8.10525 8.06652 8.59725 7.46904 8.91459 6.77481C9.04308 6.49381 9.13414 6.2147 9.18866 5.9092C9.31265 5.2146 9.27872 4.50399 9.08325 3.82238C8.86058 3.04553 8.42123 2.34292 7.83269 1.79431C7.07948 1.09232 6.12881 0.713709 5.10191 0.698959C4.74464 0.693834 4.40197 0.725209 4.05468 0.807706C3.50269 0.938828 2.9845 1.1742 2.52744 1.51094C1.77323 2.06667 1.20839 2.84466 0.929964 3.74326C0.651534 4.64186 0.659518 5.72333 1.00568 6.59656C1.15301 6.9683 1.34823 7.30229 1.5941 7.61816C2.37101 8.61626 3.3762 9.23462 4.65308 9.33587C5.67411 9.41687 6.66932 9.12262 7.49126 8.51814H7.49114Z"/><path d="M5.4946 2.71094H4.53906V3.64091H5.4946V2.71094Z"/><path d="M5.53617 4.35156H4.49219V7.46098H5.53617V4.35156Z"/></svg>
    <span class="vip-info-tip__bubble" role="tooltip">
      <span class="vip-info-tip__title">${esc(title)}</span>
      <span class="vip-info-tip__body">${esc(body)}</span>
      <a class="vip-info-tip__link" href="${esc(membershipUrl)}">${esc(linkText)}</a>
    </span>
  </span>`;
}

function buildProgressHtml(level, levelName, totalSpentUsd, progressLocale, tiersLocale, tierAssets, altTexts, esc) {
  const index = TIER_ORDER.indexOf(level);
  if (index < 0) return "";

  const fillPercent = getProgressFillPercent(level, totalSpentUsd);
  const message = buildProgressMessage(level, totalSpentUsd, progressLocale, tiersLocale, esc);

  const messageHtml = message
    ? `<div class="vip-progress__msg">${message}${buildInfoTipHtml(progressLocale, esc)}</div>`
    : "";

  const nodesHtml = TIER_ORDER.map((tier, tierIndex) => {
    const position = TRACK_POSITIONS[tier];
    if (tierIndex <= index) {
      return `<img
        class="vip-progress__ball vip-progress__ball--${tier}"
        src="${tierAssets?.[tier] || ""}"
        alt="${esc(altTexts?.[tier] || "")}"
        style="left: ${position}%"
        loading="lazy">`;
    }
    return `<span class="vip-progress__dot" style="left: ${position}%"></span>`;
  }).join("");

  const labelsHtml = TIER_ORDER.map((tier, tierIndex) => {
    const stateClass = tierIndex <= index ? "vip-progress__label--reached" : "vip-progress__label--locked";
    // last label: keep clear of the container edge on narrow tracks (half-label ≈ 3.5em)
    const position = tierIndex === TIER_ORDER.length - 1
      ? `min(${TRACK_POSITIONS[tier]}%, calc(100% - 3.5em))`
      : `${TRACK_POSITIONS[tier]}%`;
    return `<span class="vip-progress__label ${stateClass}" style="left: ${position}">${esc(tiersLocale?.[tier] || tier)}</span>`;
  }).join("");

  return `
    <div class="vip-progress">
      ${messageHtml}
      <div class="vip-progress__track" role="img" aria-label="${esc(levelName)}">
        <div class="vip-progress__rail" aria-hidden="true"></div>
        <div class="vip-progress__fill" style="width: ${fillPercent}%" aria-hidden="true"></div>
        ${nodesHtml}
      </div>
      <div class="vip-progress__labels" aria-hidden="true">${labelsHtml}</div>
    </div>
  `;
}

async function renderVipAccountInfo() {
  const memberHeaderDOM = document.querySelector(".member-header");
  const data = await window._erj_?.getAccountInfo?.();

  if (!document.contains(memberHeaderDOM)) return;

  if (!data?.profile?.vipLevel) {
    if (memberHeaderDOM) memberHeaderDOM.style.display = "none";
    return;
  }

  const { vipLevel, points, birthday, canEditBirthday } = data?.profile || {};
  const level = vipLevel?.toLowerCase();
  const esc = window._erj_.esc;
  // The loyalty profile supplies net spend in cents; convert to USD without
  // subtracting refunds again.
  // Invalid spend stays NaN so the static-fill fallback still applies.
  const totalSpentUsd = Math.max(0, parseFloat(data.profile.totalSpent) / 100);

  const {
    tier: tierLocale,
    points: pointsLocale,
    coupons: couponsLocale,
    birthday: birthdayLocale,
    edit: editLocale,
    add_birthday_gift: addBirthdayLocale,
    membership: membershipLocale,
  } = window._erj_?.getGlobalLocaleStrings()?.account || {};

  const vipLevelName = membershipLocale?.tiers?.[level] || vipLevel;
  const tierAssets = window._erj_?.vipTierAssets || {};
  const altTexts = membershipLocale?.alt_text || {};
  const firstCoupon = data?.codeRecords?.[0];
  const firstCouponName = window._erj_?.localizedRewardName?.(firstCoupon) || firstCoupon?.codeName;
  const firstCouponType = firstCoupon?.extra?.type || firstCoupon?.type || "";
  // Compact display: gift coupons (free_product) read as "Free Jewelry"; other
  // long names drop the "With Purchase Of …" suffix.
  const couponDisplayName = firstCouponType === "free_product"
    ? (window._erj_?.getGlobalLocaleStrings()?.account?.page_copy?.free_jewelry || "Free Jewelry")
    : firstCouponName
      ? firstCouponName.replace(/\s+with\s+purchase.*$/i, "") || firstCouponName
      : "";

  const editBtnHtml = `<span class="js-birthday-trigger vip-status-row__edit" role="button" tabindex="0" aria-label="${esc(editLocale)}"><svg viewBox="0 0 6 10" aria-hidden="true"><path d="M1 1l4 4-4 4"/></svg></span>`;

  let birthdayValueHtml;
  if (birthday && birthday !== "0000-00-00") {
    birthdayValueHtml = `<span class="vip-status-row__v">${esc(birthday)}${canEditBirthday ? editBtnHtml : ""}</span>`;
  } else {
    birthdayValueHtml = `<span class="js-birthday-trigger vip-status-row__v vip-status-row__add">${esc(addBirthdayLocale)}<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg></span>`;
  }

  if (memberHeaderDOM) {
    memberHeaderDOM.innerHTML = `
      <div class="vip-status-row">
        <div class="vip-status-row__seg vip-status-row__seg--tier">
          <img class="vip-status-row__badge" src="${tierAssets[level] || ""}" alt="${esc(altTexts[level] || "")}" width="24" height="24" loading="lazy">
          <div class="vip-status-row__tier-text">
            <span class="vip-status-row__k">${esc(tierLocale)}</span>
            <span class="vip-status-row__v">${esc(vipLevelName)}</span>
          </div>
        </div>
        <div class="vip-status-row__seg">
          <span class="vip-status-row__k">${esc(pointsLocale)}</span>
          <span class="vip-status-row__v vip-status-row__v--coral">${points ?? "—"}</span>
        </div>
        <div class="vip-status-row__seg vip-status-row__seg--coupon">
          <span class="vip-status-row__k">${esc(couponsLocale)}</span>
          <span class="vip-status-row__v vip-status-row__v--coupon" title="${esc(firstCouponName || "")}">${esc(couponDisplayName || "—")}</span>
        </div>
        <div class="vip-status-row__seg">
          <span class="vip-status-row__k">${esc(birthdayLocale)}</span>
          ${birthdayValueHtml}
        </div>
      </div>
      ${buildProgressHtml(level, vipLevelName, totalSpentUsd, membershipLocale?.progress, membershipLocale?.tiers, tierAssets, altTexts, esc)}
    `;

    memberHeaderDOM.querySelectorAll(".js-birthday-trigger").forEach((trigger) => {
      trigger.addEventListener("click", () => {
        window.dispatchEvent(new CustomEvent("open-birthday-modal"));
      });
    });

    // Info tooltip: tap-to-toggle on touch screens (hover handled in CSS);
    // bind once per container since renderVipAccountInfo can re-run.
    if (!memberHeaderDOM.dataset.tipBound) {
      memberHeaderDOM.dataset.tipBound = "1";
      memberHeaderDOM.addEventListener("click", (event) => {
        const tip = event.target.closest(".vip-info-tip");
        if (!tip) return;
        if (event.target.closest(".vip-info-tip__link")) return;
        event.preventDefault();
        memberHeaderDOM.querySelectorAll(".vip-info-tip.is-open").forEach((open) => {
          if (open !== tip) open.classList.remove("is-open");
        });
        tip.classList.toggle("is-open");
      });
      document.addEventListener("click", (event) => {
        if (event.target.closest(".vip-info-tip")) return;
        memberHeaderDOM.querySelectorAll(".vip-info-tip.is-open").forEach((open) => {
          open.classList.remove("is-open");
        });
      });
    }
  }

  if (!location.pathname.includes("/pages/membership")) {
    return;
  }

  const viptable = document.querySelector(".vip-benefit-table");
  if (!viptable) {
    return;
  }

  const tierConfig = [
    { tier: "Silver", index: 1, bgColor: "#FBFBFB" },
    { tier: "Gold", index: 2, bgColor: "#FEFCF8" },
    { tier: "Diamond", index: 3, bgColor: "#F9F9FA" },
    { tier: "Ambassador", index: 4, bgColor: "#FBFBFB" },
  ];

  const tier = tierConfig.find((item) => item.tier === data?.profile?.vipLevel);
  if (!tier) {
    return;
  }
  // highlight current tier
  viptable.querySelectorAll("td:nth-child(" + (tier.index + 1) + ")").forEach((td) => {
    td.style.backgroundColor = tier.bgColor;
  });
}

window._erj_.renderVipAccountInfo = renderVipAccountInfo;

window.addEventListener("DOMContentLoaded", () => {
  renderVipAccountInfo();
});
